/**
 * useJarvisStatus
 *
 * Hook der das Backend fragt, ob Jarvis für die eingeloggte Org aktiv
 * ist (jarvisAktiv-Flag in KiAgentSettings). Wenn nicht aktiv, rendert
 * der Avatar gar nicht — Standalone-Orgs und nicht-aktivierte HEYKIKI-
 * Subs sehen nichts.
 *
 * Cached das Ergebnis pro User+Org in sessionStorage. Cache-Key enthält
 * die organisationId damit nichts zwischen Org-Wechseln leakt (z.B. wenn
 * SuperAdmin „switch to org" macht oder ein User Re-Login mit anderem
 * Account in derselben Tab — sessionStorage überlebt sonst und der
 * vorherige `aktiv: true` würde der neuen Org untergeschoben).
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const CACHE_PREFIX = 'jarvis-status-cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — Settings ändern sich selten

function cacheKeyFor(orgId) {
  return orgId ? `${CACHE_PREFIX}:${orgId}` : null;
}

export function useJarvisStatus() {
  const { user } = useAuth();
  const orgId = user?.organisation?.id || null;
  const [status, setStatus] = useState(() => readCache(cacheKeyFor(orgId)));
  const [loading, setLoading] = useState(!status);

  // Fetcht den Status vom Backend (bzw. liest Cache, wenn frisch).
  // Re-runs wenn orgId wechselt (Login/Logout/Org-Switch).
  useEffect(() => {
    // Org-Wechsel: alten State verwerfen, neu laden.
    const cached = readCache(cacheKeyFor(orgId));
    if (cached) {
      setStatus(cached);
      setLoading(false);
      return undefined;
    }
    setStatus(null);
    setLoading(true);

    if (!orgId) {
      setStatus({ aktiv: false });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (!cancelled) {
            setStatus({ aktiv: false });
            setLoading(false);
          }
          return;
        }
        const resp = await axios.get(`${API_URL}/heykiki/jarvis/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          const data = resp.data || { aktiv: false };
          setStatus(data);
          writeCache(cacheKeyFor(orgId), data);
          setLoading(false);
        }
      } catch {
        // Bei Standalone-Orgs blockt heykikiRouteGuard mit 403 — wir
        // werten jeden Fehler als „nicht aktiv".
        if (!cancelled) {
          setStatus({ aktiv: false });
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [orgId]);

  // Live-Update wenn der User in der KI-Zentrale toggelt: JarvisSettings
  // dispatcht das Event `jarvis-status-changed` nach erfolgreichem PATCH;
  // wir leeren Cache + State, was den Refetch oben triggert. Header
  // (JarvisAvatarHeader) reagiert dann sofort, ohne Page-Reload.
  useEffect(() => {
    function handler(e) {
      // Wenn das Event payload mitliefert, nutzen wir es direkt — spart
      // einen Roundtrip nach jedem Toggle.
      if (e?.detail && typeof e.detail.aktiv !== 'undefined') {
        writeCache(cacheKeyFor(orgId), e.detail);
        setStatus(e.detail);
        setLoading(false);
        return;
      }
      clearCache(cacheKeyFor(orgId));
      setStatus(null);
      setLoading(true);
    }
    window.addEventListener('jarvis-status-changed', handler);
    return () => window.removeEventListener('jarvis-status-changed', handler);
  }, [orgId]);

  return { status: status || { aktiv: false }, loading };
}

function clearCache(key) {
  if (!key) return;
  try { sessionStorage.removeItem(key); } catch { /* */ }
}

function readCache(key) {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch { /* sessionStorage voll — ignore */ }
}

// Räumt alle jarvis-status-cache:* Keys auf — wird vom Logout-Flow oder
// nach Aktionen aufgerufen, die den Cache global invalidieren sollen.
export function clearJarvisStatusCache() {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* */ }
}
